import { Source, SourceOptions } from './Source';

export interface DTEDSourceOptions extends SourceOptions {
  url: string;
  level?: 0 | 1 | 2;
}

export class DTEDSource extends Source {
  public url: string;
  public level: number;

  constructor(options: DTEDSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.level = options.level ?? 0;
  }
}

export interface WMTSSourceOptions extends SourceOptions {
  url: string;
  layer: string;
  matrixSet: string;
  format?: string;
  style?: string;
  tileMatrixLabels?: string[];
  dimensions?: Record<string, string>;
}

export class WMTSSource extends Source {
  public url: string;
  public layer: string;
  public matrixSet: string;
  public format: string;
  public style: string;
  public tileMatrixLabels?: string[];
  public dimensions?: Record<string, string>;

  constructor(options: WMTSSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.layer = options.layer;
    this.matrixSet = options.matrixSet;
    this.format = options.format || 'image/png';
    this.style = options.style || 'default';
    this.tileMatrixLabels = options.tileMatrixLabels;
    this.dimensions = options.dimensions;
  }

  getTileUrl(tileMatrix: string, tileRow: number, tileCol: number): string {
    let url = this.url;
    url += (url.includes('?') ? '&' : '?');
    const params = new URLSearchParams({
      Service: 'WMTS',
      Request: 'GetTile',
      Version: '1.0.0',
      Layer: this.layer,
      Style: this.style,
      Format: this.format,
      TileMatrixSet: this.matrixSet,
      TileMatrix: tileMatrix,
      TileRow: String(tileRow),
      TileCol: String(tileCol),
    });
    if (this.dimensions) {
      for (const [key, value] of Object.entries(this.dimensions)) {
        params.set(key, value);
      }
    }
    return url + params.toString();
  }
}

export interface TMSSourceOptions extends SourceOptions {
  url: string;
  format?: string;
  flipY?: boolean;
}

export class TMSSource extends Source {
  public url: string;
  public format: string;
  public flipY: boolean;

  constructor(options: TMSSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.format = options.format || 'png';
    this.flipY = options.flipY ?? true;
  }

  getTileUrl(z: number, x: number, y: number): string {
    const tileY = this.flipY ? (Math.pow(2, z) - 1 - y) : y;
    return this.url
      .replace('{z}', String(z))
      .replace('{x}', String(x))
      .replace('{y}', String(tileY));
  }
}

export interface GPXSourceOptions extends SourceOptions {
  url?: string;
  data?: string;
}

export class GPXSource extends Source {
  public url?: string;
  public data?: string;

  constructor(options: GPXSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.data = options.data;
  }
}

export interface KMLSourceOptions extends SourceOptions {
  url?: string;
  data?: string;
  extractStyles?: boolean;
}

export class KMLSource extends Source {
  public url?: string;
  public data?: string;
  public extractStyles: boolean;

  constructor(options: KMLSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.data = options.data;
    this.extractStyles = options.extractStyles ?? true;
  }
}

export interface TopoJSONSourceOptions extends SourceOptions {
  url?: string;
  data?: any;
  objectName?: string;
}

export class TopoJSONSource extends Source {
  public url?: string;
  public data?: any;
  public objectName?: string;

  constructor(options: TopoJSONSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.data = options.data;
    this.objectName = options.objectName;
  }
}

export interface WKTSourceOptions extends SourceOptions {
  data: string;
}

export class WKTSource extends Source {
  public data: string;

  constructor(options: WKTSourceOptions) {
    super({ attributions: options.attributions });
    this.data = options.data;
  }
}

export interface ShapefileSourceOptions extends SourceOptions {
  url?: string;
  shpFile?: ArrayBuffer;
  dbfFile?: ArrayBuffer;
  encoding?: string;
}

export class ShapefileSource extends Source {
  public url?: string;
  public shpFile?: ArrayBuffer;
  public dbfFile?: ArrayBuffer;
  public encoding: string;

  constructor(options: ShapefileSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.shpFile = options.shpFile;
    this.dbfFile = options.dbfFile;
    this.encoding = options.encoding || 'UTF-8';
  }
}

export interface GeoPackageSourceOptions extends SourceOptions {
  url?: string;
  file?: ArrayBuffer;
  tableName?: string;
}

export class GeoPackageSource extends Source {
  public url?: string;
  public file?: ArrayBuffer;
  public tableName?: string;

  constructor(options: GeoPackageSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.file = options.file;
    this.tableName = options.tableName;
  }
}

export interface WFSSourceOptions extends SourceOptions {
  url: string;
  typeName: string;
  version?: string;
  maxFeatures?: number;
  outputFormat?: string;
  srsName?: string;
}

export class WFSSource extends Source {
  public url: string;
  public typeName: string;
  public version: string;
  public maxFeatures: number;
  public outputFormat: string;
  public srsName: string;

  constructor(options: WFSSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.typeName = options.typeName;
    this.version = options.version || '2.0.0';
    this.maxFeatures = options.maxFeatures || 1000;
    this.outputFormat = options.outputFormat || 'application/json';
    this.srsName = options.srsName || 'EPSG:4326';
  }

  async getFeatures(bbox?: [number, number, number, number]): Promise<GeoJSON.FeatureCollection> {
    const params = new URLSearchParams({
      service: 'WFS',
      version: this.version,
      request: 'GetFeature',
      typeName: this.typeName,
      outputFormat: this.outputFormat,
      srsName: this.srsName,
      count: String(this.maxFeatures),
    });

    if (bbox) {
      params.set('bbox', bbox.join(',') + ',' + this.srsName);
    }

    const url = this.url + (this.url.includes('?') ? '&' : '?') + params.toString();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`WFS request failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    this.emit('change');
    return data as GeoJSON.FeatureCollection;
  }
}

export interface OGCFeaturesSourceOptions extends SourceOptions {
  url: string;
  collectionId: string;
  limit?: number;
  crs?: string;
}

export class OGCFeaturesSource extends Source {
  public url: string;
  public collectionId: string;
  public limit: number;
  public crs: string;

  constructor(options: OGCFeaturesSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.collectionId = options.collectionId;
    this.limit = options.limit || 100;
    this.crs = options.crs || 'http://www.opengis.net/def/crs/OGC/1.3/CRS84';
  }

  async getFeatures(bbox?: [number, number, number, number]): Promise<GeoJSON.FeatureCollection> {
    let url = `${this.url}/collections/${this.collectionId}/items?f=json&limit=${this.limit}`;

    if (bbox) {
      url += `&bbox=${bbox.join(',')}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`OGC Features request failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    this.emit('change');
    return data as GeoJSON.FeatureCollection;
  }
}

export interface SensorSourceOptions extends SourceOptions {
  url: string;
  sensorId: string;
  observedProperty?: string;
  temporalFilter?: { start: Date; end: Date };
  updateInterval?: number;
}

export class SensorSource extends Source {
  public url: string;
  public sensorId: string;
  public observedProperty?: string;
  public temporalFilter?: { start: Date; end: Date };
  public updateInterval: number;
  private _intervalId?: ReturnType<typeof setInterval>;
  private _connected: boolean = false;

  constructor(options: SensorSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.sensorId = options.sensorId;
    this.observedProperty = options.observedProperty;
    this.temporalFilter = options.temporalFilter;
    this.updateInterval = options.updateInterval || 5000;
  }

  connect(): void {
    if (this._connected) return;
    this._connected = true;
    this.emit('connect');

    this._intervalId = setInterval(async () => {
      try {
        const response = await fetch(
          `${this.url}/Datastreams?$filter=Sensor/id eq '${this.sensorId}'`
        );
        if (response.ok) {
          const data = await response.json();
          this.emit('data', { data });
          this.emit('change');
        }
      } catch {
        this.emit('error', { error: new Error('Sensor fetch failed') });
      }
    }, this.updateInterval);
  }

  disconnect(): void {
    if (!this._connected) return;
    this._connected = false;
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = undefined;
    }
    this.emit('disconnect');
  }
}

export interface AISSourceOptions extends SourceOptions {
  url: string;
  mmsi?: string[];
  bounds?: [number, number, number, number];
  updateInterval?: number;
}

export class AISSource extends Source {
  public url: string;
  public mmsi?: string[];
  public bounds?: [number, number, number, number];
  public updateInterval: number;
  private _intervalId?: ReturnType<typeof setInterval>;
  private _connected: boolean = false;

  constructor(options: AISSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.mmsi = options.mmsi;
    this.bounds = options.bounds;
    this.updateInterval = options.updateInterval || 10000;
  }

  connect(): void {
    if (this._connected) return;
    this._connected = true;
    this.emit('connect');

    this._intervalId = setInterval(async () => {
      try {
        let url = this.url;
        const params = new URLSearchParams();
        if (this.mmsi?.length) params.set('mmsi', this.mmsi.join(','));
        if (this.bounds) params.set('bbox', this.bounds.join(','));
        const qs = params.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          this.emit('data', { data });
          this.emit('change');
        }
      } catch {
        this.emit('error', { error: new Error('AIS fetch failed') });
      }
    }, this.updateInterval);
  }

  disconnect(): void {
    if (!this._connected) return;
    this._connected = false;
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = undefined;
    }
    this.emit('disconnect');
  }
}

export interface ADSBSourceOptions extends SourceOptions {
  url: string;
  icao?: string[];
  bounds?: [number, number, number, number];
  updateInterval?: number;
}

export class ADSBSource extends Source {
  public url: string;
  public icao?: string[];
  public bounds?: [number, number, number, number];
  public updateInterval: number;
  private _intervalId?: ReturnType<typeof setInterval>;
  private _connected: boolean = false;

  constructor(options: ADSBSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.icao = options.icao;
    this.bounds = options.bounds;
    this.updateInterval = options.updateInterval || 5000;
  }

  connect(): void {
    if (this._connected) return;
    this._connected = true;
    this.emit('connect');

    this._intervalId = setInterval(async () => {
      try {
        let url = this.url;
        const params = new URLSearchParams();
        if (this.icao?.length) params.set('icao', this.icao.join(','));
        if (this.bounds) params.set('bounds', this.bounds.join(','));
        const qs = params.toString();
        if (qs) url += (url.includes('?') ? '&' : '?') + qs;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          this.emit('data', { data });
          this.emit('change');
        }
      } catch {
        this.emit('error', { error: new Error('ADS-B fetch failed') });
      }
    }, this.updateInterval);
  }

  disconnect(): void {
    if (!this._connected) return;
    this._connected = false;
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = undefined;
    }
    this.emit('disconnect');
  }
}

export interface PointCloudSourceOptions extends SourceOptions {
  url: string;
  format?: 'las' | 'laz' | 'pnts' | 'copc';
}

export class PointCloudSource extends Source {
  public url: string;
  public format: string;

  constructor(options: PointCloudSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.format = options.format || 'laz';
  }
}

export interface RealtimeSourceOptions extends SourceOptions {
  url: string;
  protocol?: 'websocket' | 'sse' | 'polling';
  updateInterval?: number;
  reconnect?: boolean;
  maxReconnectAttempts?: number;
}

export class RealtimeSource extends Source {
  public url: string;
  public protocol: 'websocket' | 'sse' | 'polling';
  public updateInterval: number;
  public reconnect: boolean;
  public maxReconnectAttempts: number;
  private _connected: boolean = false;
  private _ws?: WebSocket;
  private _eventSource?: EventSource;
  private _intervalId?: ReturnType<typeof setInterval>;
  private _reconnectAttempts: number = 0;

  constructor(options: RealtimeSourceOptions) {
    super({ attributions: options.attributions });
    this.url = options.url;
    this.protocol = options.protocol || 'websocket';
    this.updateInterval = options.updateInterval || 5000;
    this.reconnect = options.reconnect ?? true;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5;
  }

  connect(): void {
    if (this._connected) return;

    switch (this.protocol) {
      case 'websocket':
        this._connectWebSocket();
        break;
      case 'sse':
        this._connectSSE();
        break;
      case 'polling':
        this._connectPolling();
        break;
    }
  }

  private _connectWebSocket(): void {
    this._ws = new WebSocket(this.url);
    this._ws.onopen = () => {
      this._connected = true;
      this._reconnectAttempts = 0;
      this.emit('connect');
    };
    this._ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.emit('data', { data });
        this.emit('change');
      } catch {
        this.emit('data', { data: event.data });
      }
    };
    this._ws.onclose = () => {
      this._connected = false;
      this.emit('disconnect');
      this._tryReconnect();
    };
    this._ws.onerror = () => {
      this.emit('error', { error: new Error('WebSocket error') });
    };
  }

  private _connectSSE(): void {
    this._eventSource = new EventSource(this.url);
    this._connected = true;
    this._reconnectAttempts = 0;
    this.emit('connect');

    this._eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.emit('data', { data });
        this.emit('change');
      } catch {
        this.emit('data', { data: event.data });
      }
    };
    this._eventSource.onerror = () => {
      this._connected = false;
      this.emit('error', { error: new Error('SSE connection error') });
      this._eventSource?.close();
      this.emit('disconnect');
      this._tryReconnect();
    };
  }

  private _connectPolling(): void {
    this._connected = true;
    this._reconnectAttempts = 0;
    this.emit('connect');

    this._intervalId = setInterval(async () => {
      try {
        const response = await fetch(this.url);
        if (response.ok) {
          const data = await response.json();
          this.emit('data', { data });
          this.emit('change');
        }
      } catch {
        this.emit('error', { error: new Error('Polling fetch failed') });
      }
    }, this.updateInterval);
  }

  private _tryReconnect(): void {
    if (!this.reconnect) return;
    if (this._reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('error', { error: new Error('Max reconnect attempts reached') });
      return;
    }
    this._reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempts - 1), 30000);
    setTimeout(() => {
      if (!this._connected) {
        this.connect();
      }
    }, delay);
  }

  disconnect(): void {
    if (!this._connected && !this._ws && !this._eventSource && !this._intervalId) return;
    this._connected = false;

    if (this._ws) {
      this._ws.onclose = null;
      this._ws.close();
      this._ws = undefined;
    }
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = undefined;
    }
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = undefined;
    }

    this.emit('disconnect');
  }

  isConnected(): boolean {
    return this._connected;
  }
}
